// The hard close as the round's displays read it (rev 4, DESIGN §6
// "Countdowns"): the ring, the toolbar, the crash centre and Birdie's drain
// end at the board's lock, counted from the moment `race_ready` was heard on
// this page, with no device clock; after the lock a short count of X runs to
// the board's estimate of K. A display ticks, so it reads the same two signals
// the board's lock does (`betLockMoments`) at its own tick.
import { raceEventHeardAt } from 'src/store/raceEventHeard'
import type { HardClose } from 'src/types/domain/HardClose'
import type { Race } from 'src/types/domain/Race'
import { isRaceReadyEvent } from 'src/types/domain/RaceEvent'
import { betLockMoments, stageTwoAt, type BetLockMoments } from './betLock'

/**
 * The hard close of the round on the board, and where it was learnt
 * (DESIGN §2.3): from the round's own `race_ready`, or, for a board that never
 * saw it, from the game's previous round. A round whose own `race_ready` is
 * not hard is never hard, whatever the round before it was.
 */
export function roundHardClose(
  race: Pick<Race, 'raceEvents'> | undefined,
  previousRace: Pick<Race, 'raceEvents'> | undefined
): { close: HardClose; from: 'round' | 'previous' } | null {
  if (!race) return null
  const ready = race.raceEvents.find(isRaceReadyEvent)
  if (ready) {
    const close = ready.payload?.hardClose
    return close ? { close, from: 'round' } : null
  }
  const before = previousRace?.raceEvents.find(isRaceReadyEvent)?.payload?.hardClose
  return before ? { close: before, from: 'previous' } : null
}

export interface HardCloseClock extends BetLockMoments {
  close: HardClose
}

export const perfNow = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()

/** The round's lock moments now, or null when the round is not hard. */
export function hardCloseClock(
  race: Pick<Race, 'uuid' | 'raceEvents'> | undefined,
  previousRace: Pick<Race, 'raceEvents'> | undefined,
  now: number = perfNow()
): HardCloseClock | null {
  const hard = roundHardClose(race, previousRace)
  if (!race || !hard) return null
  return {
    close: hard.close,
    ...betLockMoments({
      close: hard.close,
      readyHeardAt: hard.from === 'round' ? raceEventHeardAt(race.uuid, 'race_ready') : undefined,
      startingHeardAt: raceEventHeardAt(race.uuid, 'race_starting'),
      startingPresent: race.raceEvents.some((e) => e.eventType === 'race_starting'),
      now
    })
  }
}

export interface HardCountdown {
  /** `placement`: to the lock. `sending`: the short count of X from the lock to the estimate of K. */
  stage: 'placement' | 'sending'
  /** Whole seconds left, rounded up. */
  current: number
  /** The window this count runs over, seconds. */
  animationDuration: number
  /** Elapsed fraction of that window, 0..100. */
  progress: number
  /**
   * The window on `performance.now()`, ms: placement runs from the arrival of
   * `race_ready` to the lock, sending from the lock to the estimate of K. A
   * ring draws its fill from these at any moment, not only at a tick.
   */
  startAt: number
  endAt: number
}

/**
 * What a ring or a readout counts on a hard round, or null where it has no
 * moment to count from (a late joiner: no `race_ready` heard) and the display
 * keeps today's count.
 */
export function hardCountdown(clock: HardCloseClock, now: number): HardCountdown | null {
  const lead = clock.close.lockLeadS
  if (clock.lockedAt !== undefined) {
    const leftMs = Math.max(0, clock.lockedAt + lead * 1000 - now)
    return {
      stage: 'sending',
      current: Math.ceil(leftMs / 1000),
      animationDuration: lead,
      progress: Math.min(100, Math.max(0, 100 - (leftMs / (lead * 1000)) * 100)),
      startAt: clock.lockedAt,
      endAt: clock.lockedAt + lead * 1000
    }
  }
  if (clock.lockAt === undefined || clock.close.windowS === null) return null
  const windowMs = (clock.close.windowS - lead) * 1000
  const leftMs = Math.max(0, clock.lockAt - now)
  return {
    stage: 'placement',
    current: Math.ceil(leftMs / 1000),
    animationDuration: windowMs / 1000,
    progress: Math.min(100, Math.max(0, 100 - (leftMs / windowMs) * 100)),
    startAt: clock.lockAt - windowMs,
    endAt: clock.lockAt
  }
}

/** "Last chance to bet!" shows in the last seconds of the count to the lock. */
export const LAST_CHANCE_S = 3

/**
 * What a hard round's status banner says (rev 4.1, owner 10-27). Before the
 * lock, today's lines: `last-chance` only in the last LAST_CHANCE_S of the
 * count to it, and `open` before that or with no count to read (a page that did
 * not hear `race_ready`). From the lock (`race_starting` heard, the countdown,
 * or a page that came in after `race_starting`) never `last-chance`, but stage
 * 2: `placing` while a bet of the round is unanswered, `confirming` once that
 * runs past the board's estimate of K (lock + X), then `placed` (✓),
 * `not-in-time`, or `locked` when the player has nothing on the round.
 */
export type HardBanner =
  | 'open'
  | 'last-chance'
  | 'placing'
  | 'confirming'
  | 'placed'
  | 'not-in-time'
  | 'locked'

export function hardBanner(input: {
  clock: HardCloseClock
  now: number
  /** A bet of the round is on the wire with no answer yet. */
  betInFlight: boolean
  /** The player holds a bet on the round. */
  hasBet: boolean
  /** The round's last refusal was a late answer (`NotInTime`). */
  notInTime: boolean
}): HardBanner {
  const { clock, now } = input
  if (clock.lockedBy !== undefined) {
    if (input.betInFlight) {
      const pastK =
        clock.lockedAt !== undefined &&
        now >= stageTwoAt(clock.lockedAt, clock.close.lockLeadS).confirmingAt
      return pastK ? 'confirming' : 'placing'
    }
    if (input.hasBet) return 'placed'
    return input.notInTime ? 'not-in-time' : 'locked'
  }
  const count = hardCountdown(clock, now)
  return count !== null && count.stage === 'placement' && count.current <= LAST_CHANCE_S
    ? 'last-chance'
    : 'open'
}
